import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, from, map, switchMap } from 'rxjs';
import { AppConstants } from 'src/app/commons/app-constants';
import { ListCacheService, ListCacheResult } from 'src/app/offline/list-cache.service';
import { applyLocalReservationsToGroups } from 'src/app/offline/offline-stock-reservation.util';
import { quietPlanBlockContext } from 'src/app/interceptors/quiet-plan-block';
import {
  AllocateLotRequest, ConsignmentRef, CreateLotRequest, DebitLotRequest, DispatchRequest, DispatchResult,
  FifoLayerAttribution, InventoryLot, InventoryMovement, LotAllocation, LotChainDto, LotFullChainDto, LotSortOrder, LotSourceGroup, LotStatus,
  PageResponse, PendingStockInGroup, StockAvailability, StockContributor, StockGroup, StockGroupFilter,
  UpdateLotRequest
} from '../model/inventory.model';

@Injectable({ providedIn: 'root' })
export class InventoryLotService {
  private base = `${AppConstants.API_URL}api/v1/inventory-lots`;
  private readonly listCacheService = inject(ListCacheService);

  constructor(private http: HttpClient) {}

  createLot(req: CreateLotRequest): Observable<InventoryLot> {
    return this.http.post<InventoryLot>(this.base, req);
  }

  updateLot(encodedId: string, req: UpdateLotRequest): Observable<InventoryLot> {
    return this.http.put<InventoryLot>(`${this.base}/${encodedId}`, req);
  }

  /** Batch nickname (2026-09-04) — its own tiny endpoint, deliberately not routed through
   *  updateLot() above, which overwrites supplier/purchase/notes fields even when absent from the
   *  request (see the backend's own comment on this same method). Blank/empty name clears the
   *  nickname back to "unnamed." */
  renameLot(encodedId: string, name: string): Observable<InventoryLot> {
    return this.http.patch<InventoryLot>(`${this.base}/${encodedId}/name`, { name });
  }

  // forTrace (2026-09-03 gating audit): set ONLY by the Full Trace Lot quick-view popover — every
  // other caller omits it and gets today's unchanged, unredacted response. The backend strips
  // supplier/price fields for a caller without FINANCE_ACCESS only when this flag is present.
  getLot(encodedId: string, forTrace = false): Observable<InventoryLot> {
    const params = forTrace ? new HttpParams().set('fromFullTrace', 'true') : undefined;
    return this.http.get<InventoryLot>(`${this.base}/${encodedId}`, { params });
  }

  listLots(filters: { status?: LotStatus; productId?: number; siteId?: number; supplierId?: number; sort?: LotSortOrder; page?: number; size?: number } = {}): Observable<PageResponse<InventoryLot>> {
    let params = new HttpParams();
    if (filters.status)     params = params.set('status', filters.status);
    if (filters.productId)  params = params.set('productId', filters.productId.toString());
    if (filters.siteId)     params = params.set('siteId', filters.siteId.toString());
    if (filters.supplierId) params = params.set('supplierId', filters.supplierId.toString());
    params = params.set('sort', filters.sort ?? 'newest');
    params = params.set('page', (filters.page ?? 0).toString());
    params = params.set('size', (filters.size ?? 50).toString());
    return this.http.get<PageResponse<InventoryLot>>(this.base, { params });
  }

  /** @param includeConsignmentInfo Stock Batches card source trace (2026-09-01) — opt-in only.
   *  Every other existing caller (Dispatch Wizard, Processing "select input lot", Custom-Made
   *  dialog) keeps calling this with no argument and pays zero extra backend query cost. */
  listOpenLots(includeConsignmentInfo = false, includeSizeBreakdown = false): Observable<InventoryLot[]> {
    let params = new HttpParams();
    if (includeConsignmentInfo) params = params.set('includeConsignmentInfo', 'true');
    if (includeSizeBreakdown) params = params.set('includeSizeBreakdown', 'true');
    return this.http.get<InventoryLot[]>(`${this.base}/open`, { params });
  }

  /** Offline-cached wrapper for the Custom-Made request dialog — tenant-wide, no params, so a
   *  single ListCacheService key covers it (same "cache-through, replay last-seen on failure"
   *  shape as Dashboard V7's stats fetch). Web-safe by construction: ListCacheService only ever
   *  touches offlineDb / falls back to a cached copy when isOfflineCapableNative() is true —
   *  on web this is a plain live fetch that rethrows any real error, unchanged from today.
   *  includeSizeBreakdown (Mill Output Sizes fix, 2026-09-04) always the same fixed value per
   *  caller (never varies per-row), so it just gets its own distinct cache key — same singular-key
   *  requirement as before, one key per distinct value this is ever called with. */
  listOpenLotsCached(includeConsignmentInfo = false, includeSizeBreakdown = false): Observable<ListCacheResult<InventoryLot[]>> {
    const cacheKey = includeSizeBreakdown ? 'openLotsWithSizes' : 'openLots';
    return this.listCacheService.cacheThrough(cacheKey, () => this.listOpenLots(includeConsignmentInfo, includeSizeBreakdown));
  }

  /** Stock Batches card "N deliveries"/"N consignments" tap-expand — only ever called when a
   *  user actually taps a card's expand affordance open, never eagerly for every card. */
  getLotSourceBreakdown(lotEncodedId: string): Observable<LotSourceGroup[]> {
    return this.http.get<LotSourceGroup[]>(`${this.base}/${lotEncodedId}/source-breakdown`);
  }

  /** Full Traceability Step 2, Decision 4 — Detailed Stock Tracking panel preview. Pure read: "if
   *  this cbm/pieces were drawn from this Lot right now, which contributing receipt(s) would it
   *  come from." A 1-row result means a single-source lot — caller should not show the panel. */
  getFifoPreview(lotEncodedId: string, cbm: number, pieces?: number | null): Observable<FifoLayerAttribution[]> {
    let params = new HttpParams().set('cbm', cbm.toString());
    if (pieces != null) params = params.set('pieces', pieces.toString());
    return this.http.get<FifoLayerAttribution[]>(`${this.base}/${lotEncodedId}/fifo-preview`, { params });
  }

  /** Stock Batches "find a lot" box — resolves a Lot code/name, Transport ID, Stock Unit ID, or
   *  Consignment ID/BL number/buyer order number to the matching lot(s). includeClosed=true
   *  widens the search past OPEN lots (the plain "search by ID" box's default). */
  findLots(query: string, includeClosed = false): Observable<InventoryLot[]> {
    let params = new HttpParams().set('query', query);
    if (includeClosed) params = params.set('includeClosed', 'true');
    return this.http.get<InventoryLot[]>(`${this.base}/find`, { params });
  }

  receiveTu(encodedId: string, transportUnitId: number): Observable<any> {
    return this.http.post(`${this.base}/${encodedId}/receive-tu/${transportUnitId}`, {});
  }

  allocateToDispatch(req: AllocateLotRequest): Observable<LotAllocation[]> {
    return this.http.post<LotAllocation[]>(`${this.base}/allocate`, req);
  }

  getAllocationsForTu(transportUnitId: number): Observable<LotAllocation[]> {
    return this.http.get<LotAllocation[]>(`${this.base}/allocations/tu/${transportUnitId}`);
  }

  getAllocationsForLot(encodedId: string): Observable<LotAllocation[]> {
    return this.http.get<LotAllocation[]>(`${this.base}/${encodedId}/allocations`);
  }

  debitLot(encodedId: string, req: DebitLotRequest): Observable<InventoryLot> {
    return this.http.post<InventoryLot>(`${this.base}/${encodedId}/debit`, req);
  }

  getLotMovements(encodedId: string): Observable<InventoryMovement[]> {
    return this.http.get<InventoryMovement[]>(`${this.base}/${encodedId}/movements`);
  }

  closeLot(encodedId: string): Observable<InventoryLot> {
    return this.http.post<InventoryLot>(`${this.base}/${encodedId}/close`, {});
  }

  getLotChain(encodedId: string): Observable<LotChainDto> {
    return this.http.get<LotChainDto>(`${this.base}/${encodedId}/chain`);
  }

  /** Full backward lineage (every generation back to the ultimate root ancestor) — replaces
   *  getLotChain() for the "View Chain" panel's single-hop-only view. */
  getFullLotChain(encodedId: string): Observable<LotFullChainDto> {
    return this.http.get<LotFullChainDto>(`${this.base}/${encodedId}/full-chain`);
  }

  generateCode(): Observable<string> {
    return this.http.get(`${this.base}/generate-code`, { responseType: 'text' });
  }

  /** Browse-first stock view for the Dispatch wizard. All filters optional. */
  /** quiet = true: a "your plan does not include Inventory" refusal does not open the plan dialog (the caller, Quick
   *  Search, handles it and just leaves Stock out). Every other caller keeps the dialog. */
  getStockGroups(filter: StockGroupFilter = {}, quiet = false): Observable<StockGroup[]> {
    let params = new HttpParams();
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get<StockGroup[]>(`${this.base}/stock-groups`, quiet ? { params, context: quietPlanBlockContext() } : { params });
  }

  /** Quick Search "PENDING STOCK-IN" bucket — same filter shape as getStockGroups() (siteId is
   *  harmlessly ignored server-side; Quick Search's grammar has no site concept anyway). */
  getPendingStockIn(filter: StockGroupFilter = {}, quiet = false): Observable<PendingStockInGroup[]> {
    let params = new HttpParams();
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get<PendingStockInGroup[]>(`${this.base}/pending-stock-in`, quiet ? { params, context: quietPlanBlockContext() } : { params });
  }

  /** Offline-cached wrapper for the Stock Picker (tallysheet-square-list) — keyed per
   *  productId+siteId (the two dimensions the picker actually varies), so each product/site
   *  combination a user has opened while online stays available offline afterward, same
   *  "cache what's been seen" convention as product/BP/loading-site detail caching elsewhere.
   *  Deliberately ignores origin/quality/other filter fields for the cache key — the Stock
   *  Picker always requests the full unfiltered bucket set for a product+site and filters
   *  client-side (see mapStockGroupsToSizes), so those fields are never actually set by this
   *  call site; a caller that does pass them would just share the productId+siteId cache entry,
   *  which is correct since the server response is the same underlying bucket list either way. */
  getStockGroupsCached(filter: StockGroupFilter = {}): Observable<ListCacheResult<StockGroup[]>> {
    // excludeTallyMasterId is part of the key (2026-09-15): numbers with and without a tally's own
    // holds are different answers and must not be served for each other (offline cache included).
    const key = `stockGroups:${filter.productId ?? ''}:${filter.siteId ?? ''}:${filter.excludeTallyMasterId ?? ''}`;
    return this.listCacheService.cacheThrough(key, () => this.getStockGroups(filter));
  }

  // Same as getStockGroupsCached, but each bucket's availableCbm is reduced by whatever this
  // device has already locally reserved offline against that exact product/site/origin/quality
  // (queued rows the server won't know about until their owning TU actually syncs) — without
  // this, two offline rows drawing from the same bucket both see the same pre-reservation
  // number and can both "succeed" against stock that's really only available once. Pieces
  // aren't tracked per-row today (only netCbm is), so availablePieces is left as-is.
  // excludeClientUuid: the calling TU's own _offlineClientUuid (kept separate from `filter`
  // rather than folded into StockGroupFilter, since that object doubles as raw HTTP query params
  // for the underlying live fetch — this exclusion only ever applies to the local, client-side
  // reservation subtraction below, never sent to the server).
  getEffectiveStockGroupsCached(filter: StockGroupFilter = {}, excludeClientUuid?: string): Observable<ListCacheResult<StockGroup[]>> {
    return this.getStockGroupsCached(filter).pipe(
      switchMap(result => from(applyLocalReservationsToGroups(result.data, excludeClientUuid)).pipe(
        map(adjusted => ({ ...result, data: adjusted })),
      )),
    );
  }

  /** Atomic multi-line, multi-lot dispatch — replaces per-lot debit calls. */
  dispatch(req: DispatchRequest): Observable<DispatchResult> {
    return this.http.post<DispatchResult>(`${this.base}/dispatch`, req);
  }

  /** Single pre-summed stock number for the TU form's stock-availability badge — strict
   *  origin/quality matching (blank still means "any"), unlike getStockGroups(). */
  getAvailableStock(filter: StockGroupFilter = {}): Observable<StockAvailability> {
    let params = new HttpParams();
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get<StockAvailability>(`${this.base}/stock-availability`, { params });
  }

  /** Quick Search "mixed — tap for breakdown" — full per-Consignment detail for the exact lots
   *  behind one already-returned StockGroup (pass its own g.lots[].lotEncodedId list back
   *  verbatim). Only ever called when a user taps a mixed result open, never eagerly. */
  getConsignmentBreakdown(lotEncodedIds: string[]): Observable<ConsignmentRef[]> {
    let params = new HttpParams();
    lotEncodedIds.forEach(id => { params = params.append('lotEncodedIds', id); });
    return this.http.get<ConsignmentRef[]>(`${this.base}/stock-groups/consignment-breakdown`, { params });
  }

  /** Drill-down behind a "Stock Breakdown" popover row — which Transport Units contributed. */
  getStockContributors(filter: StockGroupFilter = {}): Observable<StockContributor[]> {
    let params = new HttpParams();
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get<StockContributor[]>(`${this.base}/stock-contributors`, { params });
  }
}
